import React, { useContext, useEffect, useRef } from 'react';
// UI
import {
  Box,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Formula1Hero from '../assets/heros/Formula1.png';
import Formula1HeroMobile from '../assets/heros/Formula1Mobile.png';
import LowFormula1Hero from '../assets/heros/LowFormula1.png';
import LowFormula1HeroMobile from '../assets/heros/LowFormula1Mobile.png';
import CIEandOasisImage from '../assets/formula1/CIEandOasis.png';
import box01 from '../assets/formula1/box/box01.png';
// Components & Utils
import ViewContainer from '../components/shared/ViewContainer';
import ImageLazyLoad from '../components/shared/ImageLazyLoad';
import LargeButton from '../components/shared/buttons/LargeButton';
import DownloadAppSection from '../components/shared/DownloadAppSection';
import FAQSection from '../components/shared/FAQSection';
import Carousel from '../components/carousel/Carousel';
import VenueItem from '../components/carousel/VenueItem';
import ContactForm from '../components/shared/ContactForm';
import HalfCircle from '../components/shared/HalfCircle';
import useElementOnScreen from '../hooks/useElementOnScreen';
import { benefitsData, f1BoxImages } from '../utils/formula1Data';
import { scrollIntoRef } from '../utils/util';
import { UIContext } from '../providers/UIProvider';
import ImageWithTextCard from '../components/shared/ImageWithTextCard';
import { getResponsiveObjectForFormula1 } from '../utils/carouselResponsive';
import useDocumentTitle from '../hooks/useDocumentTitle';

const defaultElementOnScreenOptions = {
  rootMargin: '0px',
  threshold: 0.95,
};

/**
 * @function Formula1
 * @author RCH010
 * @version 1.0
 * @description Formula1 view
 * This view contains promotional content for Formula1 GrandSandClub
 * Has a picture with text and a CTA, a grid with all the benefits, a carousel
 * with pictures of the box, the contact form, FAQ and downloadApp sections
 * @returns {JSX}
 */
const Formula1 = () => {
  const theme = useTheme();
  useDocumentTitle('Oasis - F1 México');
  const contactFormRef = useRef();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { setNavbarBackgroundColor } = useContext(UIContext);
  const [bannerRef, isBannerRefVisible] = useElementOnScreen(
    defaultElementOnScreenOptions
  );
  const responsiveCarouselBreakpoints = getResponsiveObjectForFormula1(theme);

  useEffect(() => {
    if (!isBannerRefVisible) {
      setNavbarBackgroundColor(theme.palette.lightBackground);
    } else {
      setNavbarBackgroundColor('transparent');
    }
  }, [
    isBannerRefVisible,
    setNavbarBackgroundColor,
    theme.palette.lightBackground,
  ]);

  return (
    <ViewContainer>
      <Box ref={bannerRef} sx={{ position: 'relative' }}>
        <ImageLazyLoad
          srcHighQuality={isMobile ? Formula1HeroMobile : Formula1Hero}
          srcLowQuality={isMobile ? LowFormula1HeroMobile : LowFormula1Hero}
          altText=''
        />
        <Stack
          justifyContent={{ xs: 'flex-end', md: 'center' }}
          alignItems={{ xs: 'center', md: 'flex-start' }}
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            top: 0,
            left: 0,
            background:
              'linear-gradient(90deg, rgba(0, 0, 0, 0.8) -5.72%, rgba(0, 0, 0, 0.2) 119.82%)',
          }}>
          <Typography
            sx={{
              opacity: 0.8,
              width: { xs: 'fit-content', md: '100%' },
              maxWidth: 'lg',
              mx: 'auto',
              p: 3,
              fontFamily: 'MuseoModerno',
              fontWeight: 600,
              fontSize: {xs:'2.5rem !important', sm: '3rem !important' ,md:'4rem !important'},
            }}>
            sold out
          </Typography>
        </Stack>
      </Box>
      <HalfCircle isLeftCircle left='-2em' top={isMobile ? '70vh' : '70em'} />
      <HalfCircle right='-2em' bottom='80em' opacity='.6' />
      <Box
        sx={{
          zIndex: 1,
          position: 'relative',
          maxWidth: (currTheme) => currTheme.breakpoints.values.lg,
          mx: 'auto',
          mt: { xs: 10, md: 15 },
          px: 3,
        }}>
        <Typography variant='h1'>Fórmula 1 2022 palcos VIP</Typography>
        <img
          src={CIEandOasisImage}
          alt='Main GrandStand Club'
          style={{ maxHeight: '3em' }}
        />
        <Grid container spacing={4} justifyContent='center'>
          <Grid
            item
            sm={12}
            md={7}
            sx={{
              py: 5,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-evenly',
            }}>
            <Typography variant='body1' sx={{ my: 4 }}>
              Vive la F1 2022 del Gran Premio de México en los palcos
              <strong> Track Side Box </strong>con tus mejores amigos, familia,
              clientes o empleados en un lugar íntimo con la mejor vista hacia
              la zona más emocionante del
              <strong> Autódromo Hermanos Rodríguez </strong>
              siendo un espacio único donde los asistentes vivirán toda la
              acción de los monoplazas a su paso por las curvas, donde tendrán
              múltiples oportunidades de rebase.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <LargeButton
                children='Contáctanos'
                onClick={() => scrollIntoRef(contactFormRef)}
              />
            </Box>
          </Grid>
          {!isMobile && (
            <Grid item md={5}>
              <ImageLazyLoad
                objectFitImage='cover'
                srcHighQuality={box01}
                altText=''
                imageStyles={{
                  borderRadius: '16px',
                }}
              />
            </Grid>
          )}
        </Grid>
        <Typography variant='h2' align='center' sx={{ mt: 15 }}>
          Beneficios Trackside Box
        </Typography>
        <Box
          sx={{
            mt: 8,
            mb: 15,
          }}>
          <Grid
            container
            spacing={isMobile ? 0 : 3}
            justifyContent='center'
            alignItems='flex-start'>
            {benefitsData.map((items) => (
              <Grid
                key={items.id}
                item
                xs={12}
                sm={6}
                md={4}
                sx={{ height: '100%' }}>
                <ImageWithTextCard
                  {...items}
                  backgroundColor='transparent'
                  textColor={theme.palette.common.white}
                  justifyContent='flex-start'
                  imageMaxHeigh={isMobile ? '2.5em' : '3.5em'}
                  imageMaxWidth={isMobile ? '3.5em' : '3.5em'}
                  imageContainerHeight={isMobile ? '2.7em' : '3.7em'}
                  textVariant={isMobile ? 'body2' : 'body1'}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
        <Typography variant='h2' align='center' sx={{ my: 8 }}>
          ¿Qué esperas para llevar la F1 al próximo nivel?
        </Typography>
        <Carousel
          carouselItems={f1BoxImages}
          Item={(props) => <VenueItem {...props} elementHeight='15em' />}
          responsive={responsiveCarouselBreakpoints}
        />
      </Box>
      <Box sx={{ my: 5, zIndex: 1, position: 'relative' }}>
        <DownloadAppSection />
        <span ref={contactFormRef} />
      </Box>
      <ContactForm />
      <FAQSection />
    </ViewContainer>
  );
};

export default Formula1;
